import { useDispatch } from "react-redux";
import { type CustomDispatch } from "~/app/store/types";
import { type RootState } from "~/app/store";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { type ChangeEvent, type FC, useCallback, useState } from "react";
import { saveConfiguratorAction } from "~/features/configurator/Linear/redux/actions";
import Modal from "~/common/components/Modal";
import { Button, Stack, TextField } from "~/common/mui";
import { useTrackLCSaveConfigurationClick } from "~/features/configurator/Linear/hooks/useTrackLCClicks";
import { apiShareConfigurationViaSalesPartnerFinder } from "~/features/configurator/Linear/api/savedConfigShare";
import { transformSavedConfig } from "~/features/configurator/Linear/savedConfigTransformer";
import { useTranslation } from "react-i18next";

export const useRequestOfferPopup = () => {
  const dispatch = useDispatch() as CustomDispatch<RootState>;

  const trackSave = useTrackLCSaveConfigurationClick();

  const { val: isModalVisible, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);

  const submitCb = useCallback(
    (name: string) => {
      dispatch(saveConfiguratorAction(name))
        .then((response) => {
          if (response) {
            trackSave();
            const items = transformSavedConfig([response]);
            items[0].isSelected = true;
            apiShareConfigurationViaSalesPartnerFinder(items);
          }
        })
        .catch((err) => {
          console.error(err);
        });
      closeModal();
    },
    [closeModal],
  );

  const component = isModalVisible ? <SaveConfigModal onClose={closeModal} onConfirm={submitCb} /> : null;

  return {
    openModal,
    component,
  };
};

interface ModalProps {
  onClose: () => void;
  onConfirm: (name: string) => void;
}

const SaveConfigModal: FC<ModalProps> = ({ onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [pristine, setPristine] = useState(true);

  const confirmCb = useCallback(() => {
    setPristine(false);
    if (!name) {
      return;
    }
    onConfirm(name);
    onClose();
  }, [name, onClose, onConfirm]);

  const updateCb = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPristine(false);
    setName(e.target.value);
  }, []);

  const showError = !pristine && !name;

  return (
    <Modal
      onClose={onClose}
      title={t("page.linear.requestOffer.header")}
      footerChildren={
        <div className="d-flex flex-justify-between flex-align-center w-100">
          <p className={"m-0 title-medium f-carbon"}>{t("editWishlistNamePopup.mandatory")}</p>
          <Stack direction={"row"} gap={1}>
            <Button variant={"text"} onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button onClick={confirmCb}>{t("linearConfigSummary.card.requestOffer")}</Button>
          </Stack>
        </div>
      }
    >
      <div className={"pb-30"}>
        <TextField
          fullWidth
          label={t("page.linear.saveConfig.label")}
          value={name}
          onChange={updateCb}
          error={showError}
          helperText={showError ? t("page.linear.saveConfig.helperLabel") : undefined}
        />
      </div>
    </Modal>
  );
};
